import { seedProducts } from '../../data/seedProducts';
import type { ProductInput } from '../../types';
import type { Product } from '../../types';
import { normalizeImages, MAX_PRODUCT_IMAGES, slugify, validateProductImageFiles } from '../../utils/product';
import { delay, mockStorageKeys, readJson, writeJson } from '../mock/storage';

type LegacyProduct = Product & { image?: string; images?: string[]; id?: number | string; slug?: string };

function resolveSlug(input: ProductInput, products: Product[], existingId?: number): string {
  const trimmed = input.slug?.trim();
  if (trimmed) return trimmed;

  const base = slugify(input.name) || 'urun';
  let candidate = base;
  let suffix = 2;

  while (
    products.some(
      (product) => product.slug === candidate && product.id !== existingId,
    )
  ) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }

  return candidate;
}

function migrateProduct(product: LegacyProduct): Product {
  const stock =
    typeof product.stock === 'number'
      ? Math.max(0, Math.floor(product.stock))
      : product.inStock
        ? 10
        : 0;

  let images = product.images;
  if (!images || images.length === 0) {
    images = product.image ? [product.image] : [];
  }

  const { image: _, ...rest } = product;

  return {
    ...rest,
    id: typeof product.id === 'number' ? product.id : Number(product.id),
    slug: product.slug?.trim() || slugify(product.name) || `urun-${product.id}`,
    images: normalizeImages(images),
    stock,
    inStock: stock > 0,
  };
}

function ensureProductsSeeded(): Product[] {
  const existing = readJson<LegacyProduct[] | null>(mockStorageKeys.products, null);

  if (existing && existing.length > 0) {
    if (
      existing.some((product) => typeof product.id !== 'number' || !product.slug?.trim())
    ) {
      writeJson(mockStorageKeys.products, seedProducts);
      return seedProducts;
    }

    const migrated = existing.map(migrateProduct);
    const needsSave = existing.some(
      (product) =>
        !product.images?.length ||
        typeof product.stock !== 'number' ||
        'image' in product ||
        !product.slug?.trim(),
    );
    if (needsSave) writeJson(mockStorageKeys.products, migrated);
    return migrated;
  }

  writeJson(mockStorageKeys.products, seedProducts);
  return seedProducts;
}

function saveProducts(products: Product[]) {
  writeJson(mockStorageKeys.products, products);
}

function nextProductId(products: Product[]): number {
  return products.reduce((max, product) => Math.max(max, product.id), 0) + 1;
}

function normalizeProduct(input: ProductInput, products: Product[], existingId?: number): Product {
  const stock = Math.max(0, Math.floor(Number(input.stock) || 0));
  const images = normalizeImages(input.images ?? []);

  return {
    id: existingId ?? 0,
    slug: resolveSlug(input, products, existingId),
    name: input.name.trim(),
    description: input.description.trim(),
    price: Number(input.price) || 0,
    currency: input.currency,
    priceLabel: input.priceLabel?.trim() || undefined,
    category: input.category.trim(),
    images,
    benefits: input.benefits.filter(Boolean),
    stock,
    inStock: stock > 0,
    specs: input.specs && Object.keys(input.specs).length > 0 ? input.specs : undefined,
    usageWarning: input.usageWarning?.trim() || undefined,
  };
}

export async function mockFetchProducts(): Promise<Product[]> {
  await delay(200);
  return ensureProductsSeeded();
}

export async function mockGetProduct(id: number): Promise<Product> {
  await delay(200);
  const product = ensureProductsSeeded().find((item) => item.id === id);
  if (!product) {
    throw new Error('Ürün bulunamadı.');
  }
  return product;
}

export async function mockGetProductBySlug(slug: string): Promise<Product> {
  await delay(200);
  const product = ensureProductsSeeded().find((item) => item.slug === slug);
  if (!product) {
    throw new Error('Ürün bulunamadı.');
  }
  return product;
}

export async function mockCreateProduct(input: ProductInput): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const product = normalizeProduct(input, products, nextProductId(products));

  const next = [...products, product];
  saveProducts(next);
  return product;
}

export async function mockUpdateProduct(id: number, input: ProductInput): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((product) => product.id === id);

  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const product = normalizeProduct(input, products, id);
  const next = [...products];
  next[index] = product;
  saveProducts(next);
  return product;
}

export async function mockDeleteProduct(id: number): Promise<void> {
  await delay();

  const products = ensureProductsSeeded();
  const next = products.filter((product) => product.id !== id);

  if (next.length === products.length) {
    throw new Error('Ürün bulunamadı.');
  }

  saveProducts(next);
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`${file.name} okunamadı.`));
    reader.readAsDataURL(file);
  });
}

export async function mockUploadProductImages(id: number, files: File[]): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const currentImages = products[index].images;
  const validationError = validateProductImageFiles(files, currentImages.length);
  if (validationError) {
    throw new Error(validationError);
  }

  if (currentImages.length + files.length > MAX_PRODUCT_IMAGES) {
    throw new Error(`Ürün başına en fazla ${MAX_PRODUCT_IMAGES} görsel yüklenebilir.`);
  }

  const uploaded = await Promise.all(files.map((file) => fileToDataUrl(file)));
  const nextProduct = {
    ...products[index],
    images: [...currentImages, ...uploaded],
  };

  const next = [...products];
  next[index] = nextProduct;
  saveProducts(next);
  return nextProduct;
}

export async function mockDeleteProductImage(id: number, url: string): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const nextImages = products[index].images.filter((image) => image !== url);
  if (nextImages.length === products[index].images.length) {
    throw new Error('Görsel bulunamadı.');
  }

  const nextProduct = {
    ...products[index],
    images: nextImages,
  };

  const next = [...products];
  next[index] = nextProduct;
  saveProducts(next);
  return nextProduct;
}
